import { useEffect, useRef, useState } from 'react';

import Cookies from 'js-cookie';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { ATLASSIAN_ACCOUNT_ID_COOKIE_KEY } from '@/feature-gates/bootstrapper';
import { updateFeatureGatesClientWithUserIdentifiers } from '@/feature-gates/initialization.tsx';
import { experiments } from '@/utils/experiment/manifest.ts';

type AccountReadinessReason =
	| 'bootstrap_missing'
	| 'account_cookie_missing'
	| 'account_already_initialized'
	| 'account_update_succeeded'
	| 'account_initialization_failed';

/** Resolve the logged-in account before mounting hooks that memoize experiment values. */
export const useLoomLandAccountReady = (): boolean => {
	const [accountReady, setAccountReady] = useState(false);
	const updatePromise = useRef<Promise<void>>();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		let mounted = true;

		const reportReadiness = (reason: AccountReadinessReason) => {
			try {
				createAnalyticsEvent({
					action: 'evaluated',
					actionSubject: 'loomLandOptInAccount',
					eventType: 'operational',
					attributes: {
						experimentName: experiments.twcg2108LoomLandOptIn.experimentName,
						reason,
					},
				}).fire();
			} catch {
				// Diagnostics must not prevent identity initialization or signup.
			}
		};

		const updateClientWithAccount = async () => {
			try {
				const bootstrap = window.featureGateBootstrapValues;
				const rawAccountId = Cookies.get(ATLASSIAN_ACCOUNT_ID_COOKIE_KEY);
				if (!bootstrap) {
					reportReadiness('bootstrap_missing');
					return;
				}
				if (!rawAccountId) {
					reportReadiness('account_cookie_missing');
					return;
				}

				// Normalize the cookie before comparing it with the raw bootstrap ID.
				const atlassianAccountId = decodeURIComponent(rawAccountId);
				if (bootstrap.identifiers.atlassianAccountId !== atlassianAccountId) {
					await updateFeatureGatesClientWithUserIdentifiers(bootstrap, { atlassianAccountId });
					reportReadiness('account_update_succeeded');
				} else {
					reportReadiness('account_already_initialized');
				}
			} catch (error) {
				reportReadiness('account_initialization_failed');
				createAnalyticsEvent({
					action: 'failed',
					actionSubject: 'AccountAwareLoomExposure.updateClientWithAccount',
					eventType: 'operational',
					attributes: { error: error instanceof Error ? error.message : '' },
				}).fire();
			}
		};

		const markReady = () => {
			if (mounted) {
				setAccountReady(true);
			}
		};

		// Reuse the update during effect replays; each effect owns its readiness callback.
		updatePromise.current ??= updateClientWithAccount();
		void updatePromise.current.then(markReady, markReady);

		return () => {
			mounted = false;
		};
	}, [createAnalyticsEvent]);

	return accountReady;
};
